import React, { useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import logo from "../Images/logo.svg";

const Navbar = ({ variant = "dark" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [serviceDropdown, setServiceDropdown] = useState(false);
  const location = useLocation();
  const isLight = variant === "light";

  const linkClass = (path) => {
    if (location.pathname === path) {
      return isLight ? "text-blue-600 border-b-2 border-blue-600" : "text-blue-400 border-b-2 border-blue-400";
    }

    return isLight ? "hover:text-blue-600 transition text-slate-700" : "hover:text-blue-300 transition text-white";
  };

  // Close mobile menu after navigation
  const closeMenus = () => {
    setIsOpen(false);
    setServiceDropdown(false);
  };

  return (
    <nav className={`absolute top-0 left-0 w-full z-50 px-6 py-4 ${isLight ? "bg-white/80 backdrop-blur-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          onClick={closeMenus}
          className="flex items-center gap-2"
        >
          <img
            src={logo}
            alt="Orryworx Logo"
            className={`w-12 h-12 object-contain ${isLight ? "brightness-100" : "brightness-0 invert"}`}
          />
          <span className={`text-3xl font-light tracking-wide ${isLight ? "text-slate-900" : "text-white"}`}>
            rryworx
          </span>
        </Link>

        {/* Desktop Menu */}
        <ul className={`hidden md:flex items-center gap-8 font-medium ${isLight ? "text-slate-700" : "text-white"}`}>
          <li className="cursor-pointer">
            <Link to="/" className={linkClass("/")}>
              Home
            </Link>
          </li>

          <li
            className="relative cursor-pointer"
            onMouseEnter={() => setServiceDropdown(true)}
            onMouseLeave={() => setServiceDropdown(false)}
          >
            <div className={`flex items-center gap-1 transition ${isLight ? "hover:text-blue-600 text-slate-700" : "hover:text-blue-300 text-white"}`}>
              Services
              <ChevronDown size={18} />
            </div>

            {serviceDropdown && (
              <div className="absolute top-full left-0 pt-2 z-50">
                <div className="bg-white shadow-xl rounded-lg w-52 py-3">
                  <ul className="flex flex-col text-gray-700">
                    <li className="px-5 py-3 hover:bg-blue-50 hover:text-blue-600 transition cursor-pointer">
                      <Link to="/services/app">
                        App Development
                      </Link>
                    </li>

                    <li className="px-5 py-3 hover:bg-blue-50 hover:text-blue-600 transition cursor-pointer">
                      <Link to="/services/web">
                        Web Development
                      </Link>
                    </li>

                    <li className="px-5 py-3 hover:bg-blue-50 hover:text-blue-600 transition cursor-pointer">
                      <Link to="/services/ai">
                        AI Development
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </li>

          <li className="cursor-pointer">
            <Link to="/portfolio" className={linkClass("/portfolio")}>
              Portfolio
            </Link>
          </li>

          <li className="cursor-pointer">
            <Link to="/blog" className={linkClass("/blog")}>
              Blog
            </Link>
          </li>

          <li className="cursor-pointer">
            <Link to="/about" className={linkClass("/about")}>
              About Us
            </Link>
          </li>

          <li className="cursor-pointer">
            <Link to="/contact" className={linkClass("/contact")}>
              Contact
            </Link>
          </li>

          <a
            href="https://api.whatsapp.com/send?phone=919911108696"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 hover:bg-green-600 text-white p-3 rounded-full transition duration-300 shadow-lg"
          >
            <FaWhatsapp size={22} />
          </a>
        </ul>

        {/* Mobile Menu Button */}
        <button
          className={isLight ? "md:hidden text-slate-900" : "md:hidden text-white"}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className={`md:hidden mt-5 rounded-lg shadow-lg p-5 ${isLight ? "bg-white border border-slate-200" : "bg-[#03152d]"}`}>
          <ul className={`flex flex-col gap-5 font-medium ${isLight ? "text-slate-800" : "text-white"}`}>
            <li>
              <Link
                to="/"
                className={linkClass("/")}
                onClick={closeMenus}
              >
                Home
              </Link>
            </li>

            <div>
              <button
                onClick={() => setServiceDropdown(!serviceDropdown)}
                className={`flex items-center gap-2 ${isLight ? "text-slate-800" : "text-white"}`}
              >
                Services
                <ChevronDown size={18} />
              </button>

              {serviceDropdown && (
                <ul className={`ml-4 mt-3 flex flex-col gap-3 text-sm ${isLight ? "text-slate-700" : "text-gray-300"}`}>
                  <li>
                    <Link
                      to="/services/app"
                      className={isLight ? "hover:text-blue-600 transition" : "hover:text-white transition"}
                      onClick={closeMenus}
                    >
                      App Development
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/services/web"
                      className={isLight ? "hover:text-blue-600 transition" : "hover:text-white transition"}
                      onClick={closeMenus}
                    >
                      Web Development
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/services/ai"
                      className={isLight ? "hover:text-blue-600 transition" : "hover:text-white transition"}
                      onClick={closeMenus}
                    >
                      AI Development
                    </Link>
                  </li>
                </ul>
              )}
            </div>

            <li>
              <Link
                to="/portfolio"
                className={linkClass("/portfolio")}
                onClick={closeMenus}
              >
                Portfolio
              </Link>
            </li>

            <li>
              <Link
                to="/blog"
                className={linkClass("/blog")}
                onClick={closeMenus}
              >
                Blog
              </Link>
            </li>

            <li>
              <Link
                to="/about"
                className={linkClass("/about")}
                onClick={closeMenus}
              >
                About Us
              </Link>
            </li>

            <li>
              <Link
                to="/contact"
                className={linkClass("/contact")}
                onClick={closeMenus}
              >
                Contact
              </Link>
            </li>

            <a
              href="https://wa.me/919911108696"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-green-400"
            >
              <FaWhatsapp size={22} />
              WhatsApp
            </a>
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;